import { QueryClient } from '@tanstack/react-query'

import { type MutationKeys } from './MutationKeys'
import { type QueryKeys } from './QueryKeys'

export class AppQueryClient extends QueryClient {
  /**
   * This method allows to control query keys safely typed. Should be used on
   * query definitions and invalidations.
   *
   * @param QueryKey key
   * @param QueryKey[T] payload
   * @returns QueryKey as array
   */
  getQueryKey<T extends keyof QueryKeys>(
    key: T,
    ...payload: QueryKeys[T] extends null ? [] : [QueryKeys[T]]
  ) {
    return payload.length ? [key, payload[0]] : [key]
  }

  /**
   * This method allows to control query keys safely typed. Should be used on
   * query definitions and invalidations.
   *
   * @param MutationKeys key
   * @param MutationKeys[T] payload
   * @returns QueryKey as array
   */
  getMutationKey<T extends keyof MutationKeys>(
    key: T,
    ...payload: MutationKeys[T] extends null ? [] : [MutationKeys[T]]
  ) {
    return payload.length ? [key, payload[0]] : [key]
  }

  invalidateQuery<T extends keyof QueryKeys>(
    key: T,
    ...payload: QueryKeys[T] extends null ? [] : [QueryKeys[T]]
  ) {
    // @ts-expect-error — TypeScript can't infer this safely but it's runtime safe
    const queryKey = this.getQueryKey(key, payload)
    this.invalidateQueries(queryKey)
  }

  resetQuery<T extends keyof QueryKeys>(
    key: T,
    ...payload: QueryKeys[T] extends null ? [] : [QueryKeys[T]]
  ) {
    // @ts-expect-error — TypeScript can't infer this safely but it's runtime safe
    const queryKey = this.getQueryKey(key, payload)
    this.resetQueries(queryKey)
  }
}
